/**
 * Transient create/change highlight.
 *
 * When Sofia creates or edits a resource, the page briefly GLOWS the new/changed
 * element so the eye lands on it, then fades. This fires in TWO situations, and
 * the trigger is deliberately decoupled from navigation so both work:
 *
 * 1. Sofia creates/edits while you're ALREADY on the resource's page (the common
 *    case — you say "make a grandma persona" while on /personas and Sofia never
 *    navigates). There is no URL change to hang a query param on, so the panel
 *    fires an in-app signal (`requestCoviHighlight(id)`, a window event) the
 *    moment the write completes; the arrival hook is mounted app-shell-wide and
 *    listens for it, then glows the matching element on the CURRENT page.
 * 2. Sofia creates/edits and then navigates you to the resource (e.g. a new
 *    metric opens its detail page). The receipt deep-link is tagged with the id
 *    via `withCoviHighlight(href, id)` — a `?covi_highlight=<id>` query param —
 *    and the destination page glows the element on arrival.
 *
 * Both paths converge on the same glow: the arrival hook finds the element by
 * `id` (or `[data-covi-highlight-id="<id>"]`), applies the glow, auto-clears it
 * after a few seconds, and (URL path only) strips the param so a reload or
 * back-navigation never re-glows.
 *
 * Styling uses Coval design tokens (see the `covi-highlight` keyframes in
 * `styles/globals.css`) and honors `prefers-reduced-motion`.
 */

import type { SofiaActionKind } from "../model/constants"

/** Query-param key that carries the id of the element to transiently highlight. */
export const COVI_HIGHLIGHT_PARAM = "covi_highlight"

/**
 * Class applied to the target element for the duration of the glow. The
 * keyframes + reduced-motion fallback live in `styles/globals.css` so the
 * animation resolves to Coval tokens and never ships model-authored CSS.
 */
export const COVI_HIGHLIGHT_CLASS = "covi-highlight-target"

/**
 * How long the glow stays on before it auto-clears (ms). Kept short so the
 * highlight reads as a transient "here it is" cue, never a persistent state.
 * The glow must not last indefinitely.
 */
export const COVI_HIGHLIGHT_DURATION_MS = 2600

/**
 * Append the highlight target id to an already-built (org-scoped) deep-link
 * href so the destination page can glow that element on arrival. Returns the
 * href unchanged when there's no href or no id to carry. Preserves any existing
 * query string / hash on the href.
 */
export function withCoviHighlight(
  href: string | undefined,
  id: string | null | undefined,
): string | undefined {
  if (!href || !id) {
    return href
  }
  const [pathAndQuery, hash] = splitHash(href)
  const separator = pathAndQuery.includes("?") ? "&" : "?"
  const query = `${COVI_HIGHLIGHT_PARAM}=${encodeURIComponent(id)}`
  return `${pathAndQuery}${separator}${query}${hash}`
}

function splitHash(href: string): [string, string] {
  const hashIndex = href.indexOf("#")
  return hashIndex === -1
    ? [href, ""]
    : [href.slice(0, hashIndex), href.slice(hashIndex)]
}

/**
 * Read the highlight target id from a query-param value. Next's router surfaces
 * a repeated param as an array; take the first usable string. Returns null when
 * absent or empty.
 */
export function readHighlightId(
  value: string | string[] | undefined,
): string | null {
  const raw = Array.isArray(value) ? value[0] : value
  return typeof raw === "string" && raw.length > 0 ? raw : null
}

/**
 * Apply the transient glow to `element` and return a cleanup function that
 * removes it. Honors `prefers-reduced-motion`: the reduced-motion path relies
 * on the CSS media query in `globals.css` to swap the keyframe animation for a
 * brief static ring, so the JS is identical either way — add the class, remove
 * it on cleanup. Safe to call with a null element (no-op).
 */
export function applyTransientHighlight(
  element: HTMLElement | null,
): () => void {
  if (!element) {
    return () => {}
  }
  element.classList.add(COVI_HIGHLIGHT_CLASS)
  return () => {
    element.classList.remove(COVI_HIGHLIGHT_CLASS)
  }
}

/** Changed fields with dedicated anchors inside a resource detail page. */
export function resolveHighlightFields(
  kind: SofiaActionKind,
  args: Record<string, unknown>,
): string[] {
  const fieldMap = HIGHLIGHT_UPDATE_FIELD_KEYS[kind]
  if (!fieldMap) {
    return []
  }
  return Object.entries(fieldMap)
    .filter(([argKey]) => args[argKey] !== undefined)
    .map(([, field]) => field)
}

/**
 * The element id a completed create/edit should glow, or null when the kind
 * isn't a highlight target (or the id can't be read). Covers CREATE and UPDATE
 * across the resource types whose pages carry a `data-covi-highlight-id` anchor
 * (personas, metrics, test sets, reports, monitors) plus dashboard widgets. The
 * id comes from the created resource (`createdId`, read from the /v1 response)
 * for creates, and from the proposal's own id argument for updates (the id
 * already rides in the update args). Kinds not listed return null and simply
 * don't glow.
 */
export function resolveHighlightId(
  kind: SofiaActionKind,
  args: Record<string, unknown>,
  createdId: string | null,
): string | null {
  // Creates glow the freshly-created resource (id read from the /v1 response).
  if (HIGHLIGHT_CREATE_KINDS.has(kind)) {
    return createdId
  }
  // Updates glow the resource that changed — its id already rides in the args
  // under a kind-specific key.
  const updateIdKey = HIGHLIGHT_UPDATE_ID_KEYS[kind]
  if (updateIdKey) {
    const value = args[updateIdKey]
    return typeof value === "string" && value.length > 0 ? value : null
  }
  return null
}

/**
 * Create kinds whose destination page carries a `data-covi-highlight-id` anchor,
 * so the freshly-created resource can glow. `create_dashboard` is intentionally
 * absent — the dashboards page has no per-dashboard anchor to target (only the
 * widget grid does, via `create_widget`).
 */
const HIGHLIGHT_CREATE_KINDS: ReadonlySet<SofiaActionKind> = new Set([
  "create_agent",
  "create_persona",
  "create_metric",
  "create_test_set",
  "add_test_case",
  "create_report",
  "create_monitor",
  "create_widget",
])

/**
 * Update kinds → the args key holding the id of the resource that changed. Kept
 * to the resource types whose list rows / detail pages carry an anchor;
 * `update_dashboard` is omitted for the same reason as `create_dashboard`.
 */
const HIGHLIGHT_UPDATE_ID_KEYS: Partial<Record<SofiaActionKind, string>> = {
  update_agent: "agent_id",
  update_persona: "persona_id",
  update_metric: "metric_id",
  update_test_set: "test_set_id",
  update_test_case: "test_case_id",
  update_report: "report_id",
  update_monitor: "monitor_id",
}

const HIGHLIGHT_UPDATE_FIELD_KEYS: Partial<
  Record<SofiaActionKind, Readonly<Record<string, string>>>
> = {
  update_persona: { persona_prompt: "persona_prompt" },
  update_metric: { prompt: "prompt" },
  update_test_case: {
    input_str: "input_str",
    description: "description",
    expected_behaviors: "expected_behaviors",
  },
}
